import { Transition, TransitionService } from '@uirouter/angularjs';

import { storeReturnUrl } from '@/react/portainer/helpers/returnUrl';
import { resolveLandingAddonPath } from '@/react/portainer/addons/resolveLandingAddonPath';

import { IAuthenticationService } from './services/types';

export enum AccessHeaders {
  Restricted = 'restricted',
  Admin = 'admin',
  EdgeAdmin = 'edge-admin',
}

type Authorizations = string[];
type Access =
  | AccessHeaders.Restricted
  | AccessHeaders.Admin
  | AccessHeaders.EdgeAdmin
  | Authorizations;

export function requiresAuthHook(
  transitionService: TransitionService,
  authService: IAuthenticationService
) {
  transitionService.onBefore({}, (transition) =>
    checkAuthorizations(transition, authService)
  );
}

// exported for tests
export async function checkAuthorizations(
  transition: Transition,
  authService: IAuthenticationService
) {
  const stateTo = transition.to();
  const $state = transition.router.stateService;

  const { access } = stateTo.data || {};
  if (!isAccess(access)) {
    return undefined;
  }

  const isLoggedIn = await authService.init();

  if (!isLoggedIn) {
    // eslint-disable-next-line no-console
    console.info(
      'User is not authenticated, redirecting to login, access:',
      access
    );
    const currentUrl =
      window.location.pathname + window.location.search + window.location.hash;
    storeReturnUrl(currentUrl);
    return $state.target('portainer.logout');
  }

  // UI-only steering (not a security boundary): users whose team denies Portainer
  // access are redirected to their add-on on navigation, mirroring login.
  if (authService.getUserDetails().portainerAccessDenied) {
    window.location.href = await resolveLandingAddonPath();
    // Cancel the in-app transition; full-page navigation is underway.
    return false;
  }

  if (typeof access === 'string') {
    if (
      access === AccessHeaders.Restricted ||
      (access === AccessHeaders.Admin && authService.isPureAdmin()) ||
      (access === AccessHeaders.EdgeAdmin && authService.isAdmin(true))
    ) {
      return undefined;
    }
    // eslint-disable-next-line no-console
    console.info(
      'User has not enough access, redirecting to home. Required access:',
      access
    );
    return $state.target('portainer.home');
  }

  if (access.length > 0 && !authService.hasAuthorizations(access)) {
    // eslint-disable-next-line no-console
    console.info(
      'User does not have the required authorizations, redirecting to home'
    );
    return $state.target('portainer.home');
  }

  return undefined;
}

function isAccess(access: unknown): access is Access {
  if (!access || (typeof access !== 'string' && !Array.isArray(access))) {
    return false;
  }

  if (Array.isArray(access)) {
    return access.every((a) => typeof a === 'string');
  }

  return ['restricted', 'admin', 'edge-admin'].includes(access);
}
